import { lazy, ComponentType } from 'react';

type LazyFactory = () => Promise<{ default: ComponentType<any> }>;

const RELOAD_KEY = 'lp_chunk_retry';

/**
 * Wraps React.lazy to handle stale chunk failures after deployments.
 *
 * When a dynamic import fails (old HTML referencing chunks that no longer exist),
 * we force a single hard reload so the browser fetches the up-to-date index.html
 * with correct chunk hashes.  A sessionStorage flag prevents infinite reload loops.
 */
export function lazyWithRetry(factory: LazyFactory) {
  return lazy(() =>
    factory().catch((error: Error) => {
      const isChunkError =
        /Failed to fetch dynamically imported module/i.test(error.message) ||
        /Failed to load module script/i.test(error.message) ||
        /Loading chunk [\d]+ failed/i.test(error.message);

      if (!isChunkError) throw error;

      const hasAlreadyRetried = sessionStorage.getItem(RELOAD_KEY);
      if (!hasAlreadyRetried) {
        sessionStorage.setItem(RELOAD_KEY, '1');
        window.location.reload();
      }

      throw error;
    })
  );
}
